/**
 * pages/CalendarPage.js
 * 
 * Calendar view with:
 * - Monthly calendar grid
 * - Task list by date
 * - Create/edit tasks
 * - Priority filtering
 */

import React, { useState, useEffect, useCallback } from 'react';
import { calendarAPI } from '../utils/api';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, isToday, startOfWeek, endOfWeek, addMonths, subMonths, parseISO } from 'date-fns';
import './CalendarPage.css';

const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const CATEGORIES = ['work', 'personal', 'health', 'fitness', 'finance', 'learning', 'social', 'other'];
const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b', '#10b981', '#ef4444', '#06b6d4'];

function TaskModal({ onClose, onSave, initial }) {
  const [form, setForm] = useState(initial || {
    title: '',
    description: '',
    due_date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    priority: 'medium',
    category: '',
    color: '#6366f1',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setError('Title required');
    setLoading(true);
    setError('');
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>{initial ? 'Edit Task' : 'New Task'}</h3>
          <button className="btn btn-ghost" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="form-group">
            <label className="form-label">Title</label>
            <input className="form-input" value={form.title} onChange={update('title')} placeholder="Task title..." required />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-input" value={form.description} onChange={update('description')} placeholder="Details..." rows={2} />
          </div>

          <div className="task-form-grid">
            <div className="form-group">
              <label className="form-label">Due Date & Time</label>
              <input type="datetime-local" className="form-input" value={form.due_date} onChange={update('due_date')} />
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select className="form-input" value={form.priority} onChange={update('priority')}>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div className="task-form-grid">
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-input" value={form.category} onChange={update('category')}>
                <option value="">Select...</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Color</label>
              <div className="color-picker">
                {COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    className={`color-dot ${form.color === c ? 'color-dot-active' : ''}`}
                    style={{ background: c }}
                    onClick={() => setForm(f => ({ ...f, color: c }))}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <><div className="spinner" /> Saving...</> : 'Save Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterPriority, setFilterPriority] = useState('');

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const monthStr = format(currentMonth, 'yyyy-MM');
      const data = await calendarAPI.tasks({ month: monthStr });
      setTasks(data.tasks);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const handleSave = async (data) => {
    if (editTask) {
      await calendarAPI.updateTask(editTask.id, data);
    } else {
      await calendarAPI.createTask(data);
    }
    setEditTask(null);
    loadTasks();
  };

  const handleToggleComplete = async (task) => {
    await calendarAPI.updateTask(task.id, { completed: !task.completed });
    loadTasks();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await calendarAPI.deleteTask(id);
    loadTasks();
  };

  // Calendar grid
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calStart = startOfWeek(monthStart);
  const calEnd = endOfWeek(monthEnd);
  const calDays = eachDayOfInterval({ start: calStart, end: calEnd });

  const tasksForDay = (day) =>
    tasks.filter(t => t.due_date && isSameDay(parseISO(t.due_date), day));

  const selectedDayTasks = tasks
    .filter(t => t.due_date && isSameDay(parseISO(t.due_date), selectedDay))
    .filter(t => !filterPriority || t.priority === filterPriority);

  const pendingCount = tasks.filter(t => !t.completed).length;
  const overdueCount = tasks.filter(t => !t.completed && t.due_date && new Date(t.due_date) < new Date()).length;

  return (
    <div className="calendar-page animate-fadein">
      <div className="page-header">
        <div>
          <h1>Calendar</h1>
          <p className="text-muted">
            {pendingCount} pending{overdueCount > 0 && <span style={{ color: 'var(--red)' }}>, {overdueCount} overdue</span>}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditTask(null); setShowModal(true); }}>+ New Task</button>
      </div>

      <div className="calendar-layout">
        {/* Calendar grid */}
        <div className="card calendar-card">
          {/* Month navigation */}
          <div className="cal-nav">
            <button className="btn btn-ghost btn-sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>‹</button>
            <h3 className="text-mono">{format(currentMonth, 'MMMM yyyy')}</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>›</button>
          </div>

          {/* Day headers */}
          <div className="cal-week-headers">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="cal-week-header">{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="cal-grid">
            {calDays.map(day => {
              const dayTasks = tasksForDay(day);
              const isSelected = isSameDay(day, selectedDay);
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const hasTasks = dayTasks.length > 0;

              return (
                <button
                  key={day.toISOString()}
                  className={`cal-day ${isSelected ? 'cal-day-selected' : ''} ${isToday(day) ? 'cal-day-today' : ''} ${!isCurrentMonth ? 'cal-day-other-month' : ''}`}
                  onClick={() => setSelectedDay(day)}
                >
                  <span className="cal-day-num">{format(day, 'd')}</span>
                  {hasTasks && (
                    <div className="cal-day-dots">
                      {dayTasks.slice(0, 3).map((t, i) => (
                        <span key={i} className="cal-dot" style={{ background: t.color || 'var(--accent)' }} />
                      ))}
                      {dayTasks.length > 3 && <span className="cal-dot-more">+{dayTasks.length - 3}</span>}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected day tasks */}
        <div className="day-panel card">
          <div className="day-panel-header">
            <h3>{format(selectedDay, 'EEEE, MMMM d')}</h3>
            <select
              className="form-input"
              style={{ width: 'auto', fontSize: '0.8rem', padding: '0.375rem 0.5rem' }}
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
            >
              <option value="">All priorities</option>
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="text-muted" style={{ padding: '1rem 0' }}>Loading...</div>
          ) : selectedDayTasks.length === 0 ? (
            <div className="day-empty">
              <div className="day-empty-icon">◻</div>
              <p>No tasks for this day</p>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowModal(true)}>Add task</button>
            </div>
          ) : (
            <div className="task-items">
              {selectedDayTasks.map(task => (
                <div key={task.id} className={`task-full-item ${task.completed ? 'task-completed' : ''}`}>
                  <button
                    className="task-check"
                    onClick={() => handleToggleComplete(task)}
                    style={{ borderColor: task.color || 'var(--accent)' }}
                    title={task.completed ? 'Mark incomplete' : 'Mark complete'}
                  >
                    {task.completed && <span style={{ color: task.color || 'var(--accent)' }}>✓</span>}
                  </button>
                  <div className="task-full-body">
                    <div className="task-full-title">{task.title}</div>
                    {task.description && <div className="task-full-desc">{task.description}</div>}
                    <div className="task-full-meta">
                      {task.due_date && <span>{format(parseISO(task.due_date), 'h:mm a')}</span>}
                      {task.category && <span className="task-cat">{task.category}</span>}
                      <span className={`badge badge-${task.priority}`}>{task.priority}</span>
                    </div>
                  </div>
                  <div className="task-full-actions">
                    <button className="btn btn-ghost btn-sm" onClick={() => { setEditTask(task); setShowModal(true); }}>✎</button>
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={() => handleDelete(task.id)}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <TaskModal
          onClose={() => { setShowModal(false); setEditTask(null); }}
          onSave={handleSave}
          initial={editTask ? {
            ...editTask,
            due_date: editTask.due_date ? format(parseISO(editTask.due_date), "yyyy-MM-dd'T'HH:mm") : '',
          } : {
            title: '', description: '', priority: 'medium', category: '', color: '#6366f1',
            due_date: format(selectedDay, "yyyy-MM-dd'T'09:00"),
          }}
        />
      )}
    </div>
  );
}
